// Fila inferior de la pestaña «Gasto»: cuentas que pierden dinero y «Detalle técnico» (servicios con nombre humano y
// de dónde sale su precio, lo apuntado frente a lo cobrado, llamadas que se cortaron, día a día y avisos técnicos).
// Es la capa de auditoría: cerrada por defecto, para que arriba solo quede la respuesta.
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { numero, SIN_VALOR } from './formato';
import {
  COMPARTIDA,
  cuadrePorProveedor,
  CUADRA,
  fecha,
  horaMinuto,
  INICIO_REGISTRO,
  nombreCuadre,
  pct,
  pctDia,
  porServicio,
  puntosDif,
  rangoEstimados,
  usd,
  type DiaGasto,
} from './logicaGasto';
import type { AvisoGastoSala, GastoSala } from './tipos';

export const FOCO = 'outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-oficina-acento';

/** Fila de una línea que despliega un panel (o una línea fija, sin acción, cuando no hay nada que desplegar). */
export function FilaPlegable({
  titulo,
  texto,
  abierta,
  alCambiar,
  controla,
}: {
  titulo: string;
  texto: string;
  abierta?: boolean;
  alCambiar?: () => void;
  controla?: string;
}) {
  const cuerpo = (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="whitespace-nowrap text-o-texto font-semibold">{titulo}</span>
      <span className="text-o-sub leading-5 text-oficina-texto3">{texto}</span>
    </span>
  );
  if (!alCambiar) {
    return <div className="flex min-h-14 w-full items-center gap-3 rounded-o-m border border-oficina-linea py-2 pl-6 pr-4">{cuerpo}</div>;
  }
  return (
    <button
      type="button"
      aria-expanded={abierta}
      aria-controls={controla}
      onClick={alCambiar}
      className={cn('flex min-h-14 w-full items-center gap-3 rounded-o-m py-2 pl-6 pr-4 text-left transition-colors', abierta ? 'bg-oficina-hover' : 'bg-oficina-elevada hover:bg-oficina-hover', FOCO)}
    >
      {cuerpo}
      <span className="grid h-10 w-10 flex-none place-items-center rounded-o-s text-oficina-texto2" aria-hidden>
        {abierta ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </span>
    </button>
  );
}

/** Las cuentas que costaron más de lo que dejan (las peores, con lo que dejan y lo que cuestan). */
export function PanelCuentas({ id, foto }: { id: string; foto: GastoSala }) {
  return (
    <section id={id} aria-label={t('salaControl.gasto.cuentas')} className="rounded-o-m bg-oficina-elevada p-6">
      <h3 className="mb-3 text-o-texto font-semibold text-oficina-texto2">{t('salaControl.gasto.cuentasPeores')}</h3>
      <table className="w-full border-collapse text-o-sub">
        <thead>
          <tr className="text-left text-o-nota text-oficina-texto3">
            <th className="h-10 border-b border-oficina-linea pr-2 font-medium">{t('salaControl.gasto.cuenta')}</th>
            <th className="h-10 border-b border-oficina-linea px-2 text-right font-medium">{t('salaControl.gasto.nosDeja')}</th>
            <th className="h-10 border-b border-oficina-linea pl-2 text-right font-medium">{t('salaControl.gasto.nosCuesta')}</th>
          </tr>
        </thead>
        <tbody>
          {foto.cuentasPerdedoras.peores.map((c) => (
            <tr key={c.cuenta}>
              <td className="h-10 max-w-[16rem] truncate border-b border-oficina-linea pr-2">{c.nombre ?? c.cuenta.slice(0, 8)}</td>
              <td className="h-10 whitespace-nowrap border-b border-oficina-linea px-2 text-right cifras">{usd(c.parte)}</td>
              <td className="h-10 whitespace-nowrap border-b border-oficina-linea pl-2 text-right cifras">{usd(c.coste)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function Nota({ children }: { children: ReactNode }) {
  return (
    <p className="mb-6 flex gap-2 rounded-o-s bg-oficina-hover px-4 py-3 text-o-sub leading-5 text-oficina-texto2">
      <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-oficina-texto3" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

function Titulo({ children, className }: { children: ReactNode; className?: string }) {
  return <h3 className={cn('mb-3 text-o-texto font-semibold leading-5 text-oficina-texto2', className)}>{children}</h3>;
}

const TD = 'h-10 whitespace-nowrap border-b border-oficina-linea px-2';

function PorServicio({ foto }: { foto: GastoSala }) {
  const filas = porServicio(foto.motores);
  const tope = Math.max(1e-9, ...filas.map((f) => f.usd));
  return (
    <div>
      <Titulo>{t('salaControl.gasto.porServicio')}</Titulo>
      <table className="w-full border-collapse text-o-sub">
        <tbody>
          {filas.map((f) => (
            <tr key={f.clave} title={f.motores.join(', ')}>
              <td className={cn(TD, 'pl-0')}>
                <span className="block">{t(`salaControl.gasto.servicios.${f.clave}` as TKey)}</span>
                <span className="block text-o-nota leading-4 text-oficina-texto3">{t(`salaControl.gasto.precio.${f.precio}` as TKey)}</span>
              </td>
              <td className={cn(TD, 'w-1/4')}>
                <span className="block h-1 rounded-sm bg-oficina-hover">
                  <i className="block h-1 rounded-sm bg-oficina-texto3" style={{ width: `${Math.max((f.usd / tope) * 100, 0.8)}%` }} />
                </span>
              </td>
              <td className={cn(TD, 'text-right cifras')}>{usd(f.usd)}</td>
              <td className={cn(TD, 'pr-0 text-right text-oficina-texto3 cifras')}>{t('salaControl.gasto.llamadas', { n: numero(f.llamadas) })}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {(foto.compensado ?? []).map((c) => (
        <p key={`${c.dia}:${c.proveedor}`} className="mt-3 text-o-sub text-oficina-texto3 cifras">
          {t('salaControl.gasto.compensadoServicio', { usd: usd(c.usd), motivo: c.motivo })}
        </p>
      ))}
    </div>
  );
}

function Cuadre({ foto }: { foto: GastoSala }) {
  const filas = cuadrePorProveedor(foto.cuadre);
  const d1 = (v: number) => `${numero(v, 1)} $`;
  return (
    <div>
      <Titulo>{t('salaControl.gasto.cuadreTitulo')}</Titulo>
      {filas.map((c) => (
        <div key={c.clave} className="border-b border-oficina-linea py-3">
          <div className="mb-1 flex items-center justify-between font-medium">
            <span>{c.proveedor === 'deepseek' ? nombreCuadre(c, t('salaControl.gasto.servicios.deepseek')) : nombreCuadre(c)}</span>
            {c.diferencia !== null ? (
              <span className="inline-flex h-6 items-center rounded-full bg-oficina-hover px-2 text-o-nota font-medium text-oficina-texto2 cifras">
                {Math.abs(c.diferencia) <= CUADRA ? `±${puntosDif(c.diferencia)} %` : `${c.diferencia < 0 ? '−' : '+'}${puntosDif(c.diferencia)} %`}
              </span>
            ) : null}
          </div>
          <p className="text-o-sub text-oficina-texto2 cifras">
            {c.real === null
              ? t('salaControl.gasto.sinCobrado', { apuntado: d1(c.apuntado) })
              : t(c.estimado ? 'salaControl.gasto.calculadoFacturado' : c.factura ? 'salaControl.gasto.apuntadoFacturado' : 'salaControl.gasto.apuntadoCobrado', { apuntado: d1(c.apuntado), cobrado: d1(c.real) })}
          </p>
          {c.diferencia !== null && c.diferencia < COMPARTIDA ? <p className="mt-1 text-o-sub text-oficina-texto3">{t('salaControl.gasto.compartida')}</p> : null}
        </div>
      ))}
      {foto.facturaOR && foto.facturaOR.otras >= 1 ? (
        <p className="mt-3 text-o-sub text-oficina-texto3 cifras">{t('salaControl.gasto.facturaOtras', { usd: d1(foto.facturaOR.otras) })}</p>
      ) : null}
    </div>
  );
}

function Cortadas({ foto, className }: { foto: GastoSala; className?: string }) {
  return (
    <div className={className}>
      <Titulo>{t('salaControl.gasto.cortadas')}</Titulo>
      <p className="text-o-texto">
        <span className="cifras">{usd(foto.abandonadas)}</span> <span className="text-oficina-texto3">{t('salaControl.gasto.cortadasDetalle')}</span>
      </p>
    </div>
  );
}

function origenDe(d: DiaGasto | null, dia: string, hoy: string): TKey {
  if (!d) return 'salaControl.gasto.origen.sinDatos';
  if (dia === hoy) return 'salaControl.gasto.origen.enCurso';
  if (d.origen === 'estimado') return d.cerrado ? 'salaControl.gasto.origen.estimadoCerrado' : 'salaControl.gasto.origen.estimadoProvisional';
  return d.cerrado ? 'salaControl.gasto.origen.registroCerrado' : 'salaControl.gasto.origen.registroProvisional';
}

function DiaADia({ foto, dias, hoy }: { foto: GastoSala; dias: Array<{ dia: string; d: DiaGasto | null }>; hoy: string }) {
  const hayProvisional = dias.some((x) => x.d && !x.d.cerrado && x.dia !== hoy);
  const NUM = cn(TD, 'text-right cifras');
  return (
    <div>
      <Titulo>{t('salaControl.gasto.diaADia')}</Titulo>
      <table className="w-full border-collapse text-o-sub">
        <thead>
          <tr className="text-left text-o-nota text-oficina-texto3">
            <th className={cn(TD, 'pl-0 font-medium')}>{t('salaControl.gasto.dia')}</th>
            <th className={cn(TD, 'text-right font-medium')}>{t('salaControl.gasto.gastado')}</th>
            <th className={cn(TD, 'text-right font-medium')}>{t('salaControl.gasto.nuestraParte')}</th>
            <th className={cn(TD, 'text-right font-medium')}>{t('salaControl.gasto.seCome')}</th>
            <th className={cn(TD, 'pr-0 font-medium')}>{t('salaControl.gasto.comoSeCalculo')}</th>
          </tr>
        </thead>
        <tbody>
          {dias.map(({ dia, d }) => (
            <tr key={dia}>
              <td className={cn(TD, 'pl-0')}>{fecha(dia)}</td>
              <td className={cn(NUM, !d && 'text-oficina-texto3')}>{d ? usd(d.total) : SIN_VALOR}</td>
              <td className={cn(NUM, !d && 'text-oficina-texto3')}>{d && d.parte !== null ? usd(d.parte) : SIN_VALOR}</td>
              <td className={cn(NUM, !d && 'text-oficina-texto3')}>{d && dia !== hoy ? pct(pctDia(d)) : SIN_VALOR}</td>
              <td className={cn(TD, 'pr-0', !d && 'text-oficina-texto3')}>
                {d?.origen === 'estimado' ? <span className="rayado-estimado mr-2 inline-block h-3 w-3 rounded-sm align-[-2px]" aria-hidden /> : null}
                {t(origenDe(d, dia, hoy))}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold">
            <td className="h-10 pr-2">{t('salaControl.gasto.total')}</td>
            <td className="h-10 px-2 text-right cifras">{usd(foto.total)}</td>
            <td className="h-10 px-2 text-right cifras">{foto.parte === null ? SIN_VALOR : usd(foto.parte)}</td>
            <td className="h-10 px-2 text-right cifras">{pct(foto.porcentaje)}</td>
            <td className="h-10 pl-2 text-oficina-texto3">{t('salaControl.gasto.diasConDatos', { n: foto.diasConDatos, m: foto.dias })}</td>
          </tr>
        </tfoot>
      </table>
      {hayProvisional ? <p className="mt-4 text-o-sub text-oficina-texto3">{t('salaControl.gasto.provisionalNota')}</p> : null}
    </div>
  );
}

function AvisosTecnicos({ avisos }: { avisos: AvisoGastoSala[] }) {
  return (
    <div className="mt-6">
      <Titulo>{t('salaControl.gasto.avisosTecnicos')}</Titulo>
      <ul className="flex flex-col gap-2 text-o-sub text-oficina-texto2">
        {avisos.map((a) => (
          <li key={a.clave} className="flex gap-2">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-oficina-texto3" aria-hidden />
            <span>{a.texto}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** El panel desplegado de «Detalle técnico». */
export const DetalleTecnico = forwardRef<
  HTMLElement,
  { id: string; foto: GastoSala; unDia: boolean; dias: Array<{ dia: string; d: DiaGasto | null }>; hoy: string }
>(function DetalleTecnico({ id, foto, unDia, dias, hoy }, ref) {
  const tecnicos = foto.avisos.filter((a) => a.nivel === 'tecnico');
  const estimados = rangoEstimados(foto);
  const servicios = foto.motores.length > 0;
  const cuadre = cuadrePorProveedor(foto.cuadre).length > 0;
  const cortadas = foto.abandonadas > 0;
  const derecha = cuadre || cortadas;
  return (
    <section ref={ref} id={id} aria-label={t('salaControl.gasto.detalle')} className="scroll-mt-4 rounded-o-m bg-oficina-elevada p-6">
      {estimados ? <Nota>{t('salaControl.gasto.notaEstimadosDetalle', { dias: estimados, inicio: fecha(INICIO_REGISTRO) })}</Nota> : null}
      {unDia && foto.estimadoAntesDe ? <Nota>{t('salaControl.gasto.notaDetalleMixto', { hora: horaMinuto(foto.estimadoAntesDe) })}</Nota> : null}
      {!unDia ? <DiaADia foto={foto} dias={dias} hoy={hoy} /> : null}
      {servicios || derecha ? (
        <div className={cn('grid gap-8', servicios && derecha && 'md:grid-cols-2', !unDia && 'mt-8')}>
          {servicios ? <PorServicio foto={foto} /> : null}
          {derecha ? (
            <div>
              {cuadre ? <Cuadre foto={foto} /> : null}
              {cortadas ? <Cortadas foto={foto} className={cuadre ? 'mt-6' : undefined} /> : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {tecnicos.length ? <AvisosTecnicos avisos={tecnicos} /> : null}
    </section>
  );
});
